<template>
  <div class="w-full">
    <Form v-slot="{ values }"
          :initial-values="initial_values"
          :validation-schema="schema"
          class="mt-2 space-y-5"
          @submit="onSubmit">
      <TabGroup
        vertical
        :selected-index="selected_tab_index"
        class="relative transition ease-in-out rounded-lg focus:outline-none bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration"
        as="div"
        @change="selected_tab_index = $event">
        <TabList
          class="flex items-start relative border-b-[1px] dark:border-dark-divider-border rounded-tl-md shadow-sm rounded-tr-md focus:outline-none">
          <Tab v-for="element in tabs_create_metric" :key="element" v-slot="{ selected }" as="template">
            <div
              :class="[selected ? '!text-blue text-[15px] font-normal' : 'font-normal']"
              class="sm:text-sm py-4 mx-[12px] transform translate-y-[3px] text-[#46545C] dark:text-common-grey hover:border-common-grey border-b-[2px] transition ease-in-out duration-300 rounded-sm relative focus:outline-none inline-flex ring-0 ring-offset-0 border-transparent cursor-pointer">
              {{ $t(element) }}
              <div
                :class="[
                  !selected ? 'scale-0' : '',
                  'absolute pointer-events-none transition transform ease-in-out duration-side-menu-transition-duration w-full  h-[2px] rounded-full  bg-light-brand-blue dark:bg-dark-brand-blue bottom-[-1px]'
                ]" />
            </div>
          </Tab>
        </TabList>
        <TabPanels class="shadow-lg">
          <TabPanel v-for="tab in tabs_create_metric"
                    v-slot="{ selected }"
                    :key="tab"
                    static
                    class="outline-none">
            <keep-alive>
              <div v-if="selected && selected_tab_index == 0" class="flex flex-col items-center w-full p-2 mt-2 space-y-5">
                <div class="flex justify-between gap-2 w-full">
                  <TextInput
                    v-model="current_metric.name"
                    :value="current_metric.name"
                    name="name"
                    :disabled="current_metric.is_read_only"
                    type="text"
                    :info-message="current_metric.id ? `reference: ${current_metric.reference_name}` : ''"
                    :validate-on-input="true"
                    :label="$t('Metric name')" />
                  <!--  <ComboBoxInput
                    v-model="current_metric.metric_tag"
                    :items="$store.state.data.metrics_tags"
                    name="metric_tag"
                    classes="w-1/3 relative dark:text-white"
                    label-attr="label"
                    value-attr="value"
                    :is-box-background="true"
                    :label="$t('Metric Tag')"
                    :show-label="true"
                    :floating-label="true"
                    :allow-null="true" /> -->
                </div>
                <TextArea
                  v-model="current_metric.description"
                  classes="!mt-6"
                  :value="current_metric.description"
                  name="description"
                  :label="$t('Description')" />
              </div>

              <Display
                v-else-if="selected && tabs_create_metric[selected_tab_index] == 'Display'"
                :metric="current_metric"
                class="p-2 mt-2"
                @update="
                  ($event) => {
                    current_metric.is_public = $event.is_public
                    current_metric.is_rs_readonly = $event.is_rs_readonly
                    current_metric.input_type = $event.input_type
                    current_metric.input_options = $event.input_options
                  }
                " />
              <Aggregation
                v-else-if="selected && tabs_create_metric[selected_tab_index] == 'Aggregations'"
                :metric="current_metric"
                class="p-2 mt-2"
                @update="
                  ($event) => {
                    current_metric.aggregation_method = $event.aggregation_method
                  }
                " />
              <Colors
                v-else-if="selected && tabs_create_metric[selected_tab_index] == 'Colors'"
                :metric="current_metric"
                class="p-2 mt-2"
                @update="
                  ($event) => {
                    current_metric.metric_colors = $event.metric_colors
                  }
                " />
              <DataSources
                v-else-if="selected && tabs_create_metric[selected_tab_index] == 'Data Sources' && current_metric?.data_sources?.length > 0"
                :metric="current_metric"
                class="p-2 mt-2"
                @update="
                  ($event) => {
                    current_metric.data_sources = $event.data_sources
                  }
                " />
            </keep-alive>
          </TabPanel>
        </TabPanels>

        <div class="flex gap-2 p-2 mt-7 justify-evenly">
          <button type="button" class="w-1/2 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
          <button type="submit" :disabled="values.name == ''" class="w-1/2 primary-btn disabled:opacity-50">{{ $t("Update") }}</button>
        </div>
      </TabGroup>
    </Form>
  </div>
</template>

<script>
import { Form } from "vee-validate"
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue"
import Display from "@/views/dashboard/admin/metrics/form-tabs/Display.vue"
import Colors from "@/views/dashboard/admin/metrics/form-tabs/Colors.vue"
import Aggregation from "@/views/dashboard/admin/metrics/form-tabs/Aggregation.vue"
import DataSources from "@/views/dashboard/admin/metrics/form-tabs/DataSources.vue"
import TextInput from "@/components/shared/TextInput.vue"
// import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
export default {
  components: {
    TextInput,
    TextArea,
    Form,
    Tab,
    TabGroup,
    TabList,
    TabPanel,
    TabPanels,
    Display,
    Colors,
    Aggregation,
    // ComboBoxInput,
    DataSources
  },
  props: {
    metric: {
      type: Object,
      required: true
    }
  },
  emits: ["cancel", "done"],
  data() {
    return {
      selected_tab_index: 0,
      tabs_create_metric: ["Name and Description", "Display", "Aggregations", "Colors", "Data Sources"],
      current_metric: null,
      schema: {},
      initial_values: {}
    }
  },
  created() {
    this.current_metric = utils.clone(this.metric)
    this.initial_values = { ...this.current_metric }
    if (this.current_metric && this.current_metric.data_type !== "Number") {
      if (this.current_metric?.data_sources?.length > 0) {
        this.tabs_create_metric = ["Name and Description", "Display", "Data Sources"]
      } else {
        this.tabs_create_metric = ["Name and Description", "Display", "Aggregations"]
      }
    }
  },
  mounted() {
    this.current_metric = utils.clone(this.metric)
    this.initial_values = { ...this.current_metric }
    if (this.current_metric && this.current_metric.data_type !== "Number") {
      if (this.current_metric?.data_sources?.length > 0) {
        this.tabs_create_metric = ["Name and Description", "Display", "Data Sources"]
      } else {
        this.tabs_create_metric = ["Name and Description", "Display", "Aggregations"]
      }
    }
  },

  methods: {
    onSubmit() {
      this.$store.dispatch("data/updateMetric", this.current_metric).then((res) => {
        this.$emit("done")
        this.$emitter.emit(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_OVERVIEW_COMPONENT, res)
        this.$emitter.emit(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS, res)
        this.$emitter.emit(ALL_EMITTERS.RELOAD_METRICS_AND_SOURCES)
      })
    }
  }
}
</script>
